import { get } from "lodash-es";
import { FC, useEffect, useState } from "react";
import { v4 } from "uuid";

import CloseIcon from "@mui/icons-material/Close";
import {
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import SaveButton from "../../../../../components/SaveButton";
import useNavigateWithSearch from "../../../../../hooks/use-navigate-with-search";
import { pushError, pushSuccess } from "../../../../../slices/notifications";
import {
  setFormRoute,
  setRouteFormProperties,
} from "../../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../../store";
import {
  Route,
  useCreateKeyPairMutation,
  useCreateRouteMutation,
  useDeleteKeyPairMutation,
  useListDefaultTemplatesQuery,
} from "../../../../../store/pomeriumApi";
import { formUpdater } from "../../../../../types/form";
import { likelyPemEncodedCertificate } from "../../../../../validators/certs";
import { RouteSettings } from "../RouteSettings";
import { AddCaCert } from "./AddCaCert";
import { AddConfig } from "./AddConfig";
import { ChooseSetupType } from "./ChooseSetupType";
import { ConfigurePermissions } from "./ConfigurePermissions";
import { ServiceAccountToken } from "./ServiceAccountToken";
import useReadOnly from "@/hooks/use-readonly";

export type setupType = "token" | "config";

const steps = {
  token: [
    "Choose setup type",
    "Route settings",
    "Add CA certificate",
    "Configure permissions",
    "Service account token",
  ],
  config: [
    "Choose setup type",
    "Route settings",
    "Add CA certificate",
    "Add config",
  ],
};

const KubernetesSetupDialog: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithSearch();
  const [setupType, setSetupType] = useState<setupType>("token");
  const [step, setStep] = useState(0);
  const [cert, setCert] = useState<string>("");
  const { form, formErrors } = useSelector((state) => state.routes);
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [keypairId, setKeypairId] = useState<string>("");
  const [refresh, setRefresh] = useState(v4());
  const [createCert] = useCreateKeyPairMutation();
  const [addRoute] = useCreateRouteMutation();
  const [deleteCert] = useDeleteKeyPairMutation();
  const lastStep = step === steps[setupType].length - 1;
  const readonly = useReadOnly({ clusterId, organizationId });

  const getContent = () => {
    switch (steps[setupType][step]) {
      case "Route settings":
        return <RouteSettings refresh={refresh} readonly={readonly} />;
      case "Add CA certificate":
        return <AddCaCert cert={cert} setCert={setCert} />;
      case "Configure permissions":
        return <ConfigurePermissions />;
      case "Service account token":
        return <ServiceAccountToken />;
      case "Add config":
        return <AddConfig />;
      default:
        return <ChooseSetupType chooseSetupType={chooseSetupType} />;
    }
  };

  const { data: defaultRoute, isLoading: defaultLoading } =
    useListDefaultTemplatesQuery("route");

  useEffect(() => {
    if (defaultRoute) {
      dispatch(
        setFormRoute(
          get(defaultRoute, "0.defaultProperties", {}) as Partial<Route>,
        ),
      );
    }
  }, [defaultRoute, defaultLoading]);

  const chooseSetupType = (option: setupType) => {
    setSetupType(option);
    handleNext();
  };

  const handleSubmit = async () => {
    try {
      const resp = await addRoute({
        organizationId,
        routeProperties: { ...form, namespaceId: clusterId } as Route,
      }).unwrap();
      if (resp?.id) {
        dispatch(pushSuccess("Route successfully saved!"));
        navigate("/app/management/routes", { replace: true });
      }
    } catch {
      dispatch(pushError("There was a problem saving the route."));
    }
  };

  async function handleAddingCert() {
    const updates: formUpdater[] = [];
    try {
      if (cert) {
        const keypair = await createCert({
          organizationId,
          keyPairWithKeyProperties: {
            namespaceId: clusterId,
            certificate: cert,
            name: `kube-root-ca-${window.crypto.randomUUID().substring(0, 8)}`,
          },
        }).unwrap();
        updates.push({ path: "tlsCustomCaKeyPairId", value: keypair.id });
        setKeypairId(keypair.id);
      }
      updates.push({ path: "allowSpdy", value: true });
      updates.push({ path: "allowWebsockets", value: true });
      dispatch(setRouteFormProperties(updates));
      setStep((step) => step + 1);
    } catch {
      dispatch(pushError(`Error creating certificate`));
    }
  }

  async function handleNext() {
    if (lastStep) {
      await handleSubmit();
    } else if (steps[setupType][step] === "Add CA certificate") {
      await handleAddingCert();
    } else {
      setStep((step) => step + 1);
    }
  }
  function handleBack() {
    if (steps[setupType][step] === "Route settings") {
      setRefresh(v4());
    }
    if (step > 0) {
      setStep(step - 1);
    }
  }

  const onCancel = () => {
    if (keypairId) {
      deleteCert({
        organizationId,
        keyPairId: keypairId,
      });
    }
    navigate(-1);
  };

  const onClose = (_: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      onCancel();
    }
  };

  const isNextDisabled = (): boolean => {
    switch (steps[setupType][step]) {
      case "Route settings":
        return (
          !!formErrors.length || !form?.name || !form?.to?.length || !form?.from
        );
      case "Add CA certificate":
        return !likelyPemEncodedCertificate(cert);
      default:
        return false;
    }
  };

  const navigateToEdit = (): void => {
    navigate("/app/management/routes/create?routePrepopulated=true", {
      replace: true,
    });
  };

  return (
    <Dialog open={true} maxWidth={"lg"} scroll="paper" onClose={onClose}>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        Kubernetes route setup
        <IconButton
          color="primary"
          onClick={onCancel}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stack direction="row" spacing={5}>
          {step > 0 && (
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Stepper activeStep={step} orientation="vertical">
                  {steps[setupType].map((label, i) => {
                    return (
                      <Step key={i}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          )}
          {getContent()}
        </Stack>
      </DialogContent>

      <DialogActions>
        {step > 0 && !lastStep && (
          <>
            <Button onClick={onCancel}>Cancel</Button>
            <Button onClick={handleBack}>Back</Button>
            <SaveButton
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              Next
            </SaveButton>
          </>
        )}
        {lastStep && (
          <>
            <Button onClick={navigateToEdit}>Continue Editing</Button>
            <SaveButton
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              Save Route
            </SaveButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default KubernetesSetupDialog;
